import React, { useEffect, useMemo, useState } from "react";
import { Container } from "@material-ui/core";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import CheckIcon from "@material-ui/icons/Check";
import FlightTakeoffIcon from "@material-ui/icons/FlightTakeoff";
import { Button } from "../../components/common";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import appRoutes from "../../routes/app.routes";
import ArticleCard from "./components/ArticleCard";
import BlogFaq from "./components/BlogFaq";
import BlogHero from "./components/BlogHero";
import BlogIcon from "./components/BlogIcon";
import CategoryList from "./components/CategoryList";
import NewsletterSignup from "./components/NewsletterSignup";
import { categories, faqs, guides } from "./data/blog.mock";
import { Article } from "./types";
import { fetchBlogArticles } from "../../services/blog.services";

const Blog = () => {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [showAllLatest, setShowAllLatest] = useState(false);
  const [search, setSearch] = useState("");
  const [articles, setArticles] = useState<Article[]>([]);
  const [featuredArticles, setFeaturedArticles] = useState<Article[]>([]);
  const [loadingArticles, setLoadingArticles] = useState(true);
  const [articleError, setArticleError] = useState("");

  useEffect(() => {
    const loadArticles = async () => {
      try {
        setLoadingArticles(true);
        setArticleError("");
        const response = await fetchBlogArticles();
        setArticles(response.articles);
        setFeaturedArticles(response.featuredArticles);
      } catch (error) {
        setArticleError("Unable to load articles right now. Please try again later.");
      } finally {
        setLoadingArticles(false);
      }
    };

    loadArticles();
  }, []);

  const filteredArticles = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return articles.filter((article) => {
      const categoryMatches = !activeCategory || article.category === activeCategory;
      const searchMatches = !normalizedSearch || [article.title, article.excerpt, article.category, article.author]
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearch);

      return categoryMatches && searchMatches;
    });
  }, [activeCategory, articles, search]);

  const visibleArticles = showAllLatest || activeCategory ? filteredArticles : filteredArticles.slice(0, 4);

  const selectCategory = (category: string | null) => {
    setActiveCategory(category);
    setShowAllLatest(Boolean(category));
  };

  return (
  <main className="blog-page">
    <Helmet>
      <title>Career Advice, Job Search & Inclusive Hiring | employHER Blog</title>
      <meta name="description" content="Explore practical career advice, job-search guidance, workplace insights and inclusive hiring resources from employHER." />
    </Helmet>
    <Container fixed className="blog-page__container">
      <BlogHero search={search} onSearchChange={setSearch} />

      {featuredArticles.length > 0 && <section className="blog-section blog-featured" aria-labelledby="featured-title">
        <div className="blog-section__heading blog-section__heading--split">
          <h2 id="featured-title">Featured Articles</h2>
          <p>Hand-picked stories to inspire your career</p>
        </div>
        <div className="blog-featured__grid">
          <ArticleCard article={featuredArticles[0]} variant="featured" />
          <div className="blog-featured__list">
            {featuredArticles.slice(1).map((article) => (
              <ArticleCard key={article.id} article={article} variant="featured-small" />
            ))}
          </div>
        </div>
      </section>}

      <section className="blog-section blog-category-section" aria-labelledby="categories-title">
        <div className="blog-section__heading blog-section__heading--center">
          <h2 id="categories-title">Browse by Category</h2>
        </div>
        <CategoryList
          categories={categories}
          activeCategory={activeCategory}
          onCategoryChange={selectCategory}
        />
      </section>

      <section className="blog-section blog-latest" aria-labelledby="latest-title">
        <div className="blog-section__heading blog-section__heading--split">
          <div>
            <h2 id="latest-title">{activeCategory || (search.trim() ? "Search Results" : "Latest Articles")}</h2>
            {activeCategory && <p className="blog-latest__filter-note">Showing articles in {activeCategory}</p>}
            {!activeCategory && search.trim() && <p className="blog-latest__filter-note">Showing results for “{search.trim()}”</p>}
          </div>
          <button type="button" className="blog-text-link" onClick={() => setShowAllLatest(!showAllLatest)}>
            {showAllLatest ? "Show Less" : "View All Articles"} <ArrowForwardIcon fontSize="small" />
          </button>
        </div>
        <div className="blog-latest__grid">
          {visibleArticles.map((article) => <ArticleCard key={article.id} article={article} />)}
        </div>
        {loadingArticles && <p className="blog-latest__filter-note">Loading articles...</p>}
        {articleError && <p className="blog-latest__filter-note">{articleError}</p>}
        {!loadingArticles && !articleError && visibleArticles.length === 0 && (
          <p className="blog-latest__filter-note">No articles found for this selection.</p>
        )}
      </section>

      <section className="blog-guides" aria-labelledby="guides-title">
        <h2 id="guides-title">Popular Guides</h2>
        <div className="blog-guides__grid">
          {guides.map((guide) => (
            <button
              className="blog-guide-card"
              key={guide.title}
              type="button"
              onClick={() => selectCategory(guide.category)}
              aria-label={`Browse ${guide.category} articles`}
            >
              <span className="blog-guide-card__icon"><BlogIcon name={guide.icon} /></span>
              <h3>{guide.title}</h3>
              <p>{guide.description}</p>
              <span aria-hidden="true">→</span>
            </button>
          ))}
        </div>
      </section>

      <section className="blog-audiences" aria-label="Blog resources by audience">
        <article className="blog-audience-panel blog-audience-panel--seekers">
          <div>
            <h2>For Job Seekers</h2>
            <p>Expert advice to help you find jobs, build skills and grow your career.</p>
            <ul>
              <li><CheckIcon /> Find the right job</li>
              <li><CheckIcon /> Improve your resume</li>
              <li><CheckIcon /> Prepare for interviews</li>
              <li><CheckIcon /> Upskill and grow</li>
            </ul>
            <Button component={Link as any} to={appRoutes.candidateJobSearch.path}>Browse Jobs</Button>
          </div>
          <img src="https://images.unsplash.com/photo-1551836022-4c4c79ecde51?auto=format&fit=crop&w=700&q=85" alt="Professional using a tablet" />
        </article>
        <article className="blog-audience-panel blog-audience-panel--employers">
          <div>
            <h2>For Employers</h2>
            <p>Insights and strategies to hire better, build inclusive teams and grow your organisation.</p>
            <ul>
              <li><CheckIcon /> Hire the right talent</li>
              <li><CheckIcon /> Build inclusive practices</li>
              <li><CheckIcon /> Strengthen employer branding</li>
              <li><CheckIcon /> Stay ahead of workplace trends</li>
            </ul>
            <Button component={Link as any} to={appRoutes.salesInquiry.generatePath()} color="rounded">Hire Talent</Button>
          </div>
          <img src="https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=700&q=85" alt="Professional at work" />
        </article>
      </section>

      <NewsletterSignup />
      <BlogFaq faqs={faqs} />

      <section className="blog-final-cta">
        <span className="blog-final-cta__icon"><FlightTakeoffIcon /></span>
        <div>
          <h2>Ready to take the next step?</h2>
          <p>Join employHER today and unlock more opportunities.</p>
        </div>
        <div className="blog-final-cta__actions">
          <Button component={Link as any} to={appRoutes.userSignUp.path}>Create Your Profile</Button>
          <Button component={Link as any} to={appRoutes.salesInquiry.generatePath()} color="rounded">Hire Talent</Button>
        </div>
      </section>
    </Container>
  </main>
  );
};

export default Blog;
